<template>
  <div>
    <AppTableHeader>
      <template #right>
        <SanctionDialog
          v-if="$auth.checkUserCanPerformMutationsInScopeAny('sanctionCreate')"
          ref="sanctionDialog"
          :values="values"
          :sanctioned-entity="sanctionedEntity"
          :refetch-query-name="refetchQueryName"
        >
          <template #activator="{ dialog }">
            <v-btn
              class="btn-with-icon mx-2 text-white"
              variant="text"
              data-test="issueSanction"
              @click="dialog.dialogVisibility()"
            >
              <div class="bg-edit btn-icon-with-text">
                <v-icon size="small" class="text-white"> $iconPlus </v-icon>
              </div>
              <span class="text-white">{{
                $t("common:button.add", "Add", "Add button")
              }}</span>
            </v-btn>
          </template>
        </SanctionDialog>
      </template>
    </AppTableHeader>
    <v-data-table
      :headers="mixedHeaders"
      :items="sanctions"
      :items-per-page="-1"
      :hide-default-header="!$vuetify.display.mdAndUp"
      :loading="loading || $apollo.queries.sanctionReasons.loading"
      hide-default-footer
      class="entsys--table"
    >
      <template #item="{ item, toggleExpand, internalItem, isExpanded, index }">
        <tr :data-test="`sanction-${item.id}`">
          <template v-if="$vuetify.display.mdAndUp">
            <td>
              <div
                v-if="item.internalComment"
                :aria-label="$t('common:commentIcon.alt')"
              >
                <v-icon color="primary">$iconCommentDots</v-icon>
              </div>
            </td>
            <td>
              {{ $dayjs.date(item.validFrom) }} -
              {{ $dayjs.date(item.validUntil) }}
              <div v-if="!item.approved">
                <AppChip color="edit" text-color="text-white">
                  {{
                    $t(
                      "sanctions:SanctionsManagementPage.badge.notApproved.label",
                      "Not approved"
                    )
                  }}
                </AppChip>
              </div>
            </td>
            <td>
              {{ item.name }}
              <div>
                {{ item.sanctionReasons.map(x => x.name).join(", ") }}
              </div>
            </td>
            <td>
              <div class="remove-paragraph-margin" v-html="item.description" />
              <template v-if="item.publicComment">
                <div
                  class="remove-paragraph-margin"
                  v-html="item.publicComment"
                />
              </template>
            </td>
          </template>
          <template v-else>
            <td colspan="99">
              <v-col class="pb-0">
                <div class="text-muted mb-2">
                  {{ headers[0].title }}
                </div>

                <div class="d-flex">
                  <div
                    v-if="item.internalComment"
                    :aria-label="$t('common:commentIcon.alt')"
                    class="mr-2"
                  >
                    <v-icon color="primary">$iconCommentDots</v-icon>
                  </div>

                  {{ $dayjs.date(item.validFrom) }} -
                  {{ $dayjs.date(item.validUntil) }}
                </div>
                <div v-if="!item.approved">
                  <AppChip color="edit" text-color="text-white">
                    {{
                      $t(
                        "sanctions:SanctionsManagementPage.badge.notApproved.label",
                        "Not approved"
                      )
                    }}
                  </AppChip>
                </div>
              </v-col>
              <v-col class="pb-0">
                <div class="text-muted mb-2">
                  {{ headers[1].title }}
                </div>

                {{ item.name }}
                <div>
                  {{ item.sanctionReasons.map(x => x.name).join(", ") }}
                </div>
              </v-col>
              <v-col class="pb-0">
                <div class="text-muted mb-2">
                  {{ headers[2].title }}
                </div>

                <div
                  class="remove-paragraph-margin"
                  v-html="item.description"
                />
                <template v-if="item.publicComment">
                  <div
                    class="remove-paragraph-margin"
                    v-html="item.publicComment"
                  />
                </template>
              </v-col>
            </td>
          </template>
          <td>
            <AppButtonPositionSlot>
              <v-tooltip
                v-if="
                  $allowedMutation(item.allowedMutations, 'sanctionApprove')
                "
                location="bottom"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-btn-icon
                    v-bind="tooltipProps"
                    data-test="approve"
                    class="bg-edit-green"
                    icon
                    :disabled="mutationLoading"
                    :aria-label="
                      $t(
                        'sanctions:SanctionsManagementPage.approveButton.label'
                      )
                    "
                    size="small"
                    :loading="discardApproveLoading == `approve-${index}`"
                    @click="sanctionApprove(item, refetchQuery)"
                  >
                    <v-icon size="small"> $iconCheck </v-icon>
                  </v-btn-icon>
                </template>
                <span>
                  {{
                    $t("sanctions:SanctionsManagementPage.approveButton.label")
                  }}</span
                >
              </v-tooltip>

              <v-btn-icon
                v-if="$allowedMutation(item.allowedMutations, 'sanctionUpdate')"
                icon
                class="bg-edit-green"
                data-test="update"
                :aria-label="$t('common:editButton.alt')"
                size="small"
                @click="sanctionEdit(item)"
              >
                <v-icon size="small" color="white"> $edit </v-icon>
              </v-btn-icon>
              <v-tooltip
                v-if="
                  $allowedMutation(item.allowedMutations, 'sanctionDiscard')
                "
                location="bottom"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-btn-icon
                    v-bind="tooltipProps"
                    data-test="discard"
                    class="bg-remove"
                    :aria-label="
                      $t(
                        'sanctions:SanctionsManangementPage.discardButton.label'
                      )
                    "
                    icon
                    size="small"
                    :disabled="mutationLoading"
                    :loading="discardApproveLoading == `discard-${index}`"
                    @click="sanctionDiscard(item, refetchQuery)"
                  >
                    <v-icon size="small"> $iconTrash </v-icon>
                  </v-btn-icon>
                </template>
                <span>
                  {{
                    $t("sanctions:SanctionsManangementPage.discardButton.label")
                  }}</span
                >
              </v-tooltip>

              <v-btn-expand
                v-if="item.internalComment"
                :model-value="isExpanded(internalItem)"
                @click="toggleExpand(internalItem)"
              >
              </v-btn-expand>
            </AppButtonPositionSlot>
          </td>
        </tr>
      </template>

      <template #expanded-row="{ item }">
        <tr class="pa-0">
          <td class="bg-strong" colspan="99">
            <v-row class="mx-0">
              <v-col cols="12" md="4">
                <b>{{
                  $t(
                    "sanctions:SanctionsManagementPage.internalComment.label",
                    "Internal comment"
                  )
                }}</b>
                <br />
                <div v-html="item.internalComment" />
              </v-col>
            </v-row>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { ref } from "vue";
import gql from "fraql";
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppChip from "@elements/AppChip.vue";
import SanctionDialog from "./SanctionDialog.vue";
import AppButtonPositionSlot from "@elements/AppButtonPositionSlot.vue";
import { useSanctionManagementHelper } from "@/components/admin/sanctionApprovals";

export default {
  name: "SanctionsTable",
  components: {
    AppTableHeader,
    AppChip,
    SanctionDialog,
    AppButtonPositionSlot
  },
  props: {
    personId: { type: String, default: null },
    horseId: { type: String, default: null },
    sanctionedEntity: { type: String, default: null },
    refetchQueryName: { type: String, required: true },
    loading: { type: Boolean, default: false },
    sanctions: { type: Array, required: true },
    headers: { type: Array, default: () => [] }
  },
  setup() {
    const mutationLoading = ref(false);

    const { discardApproveLoading, sanctionApprove, sanctionDiscard } =
      useSanctionManagementHelper(mutationLoading);

    return {
      mutationLoading,
      discardApproveLoading,
      sanctionApprove,
      sanctionDiscard
    };
  },
  data() {
    return { sanctionReasons: [] };
  },
  computed: {
    mixedHeaders() {
      return [
        { title: "", sortable: false },
        ...this.headers,
        { title: "", sortable: false }
      ];
    },
    values() {
      if (this.personId) {
        return { sanctionedPersonId: this.personId };
      }
      if (this.horseId) {
        return { sanctionedHorseId: this.horseId };
      }
      return {};
    }
  },
  fragments: {
    sanctionFields: gql`
      fragment _ on Sanction {
        id
        sanctionDefinitionId
        name
        createdAt
        validFrom
        validUntil
        publicComment
        internalComment
        description

        sanctionedEntity
        sanctionReasonIds
        sanctionReasons @client {
          id
          name
        }
        approved
        approvedAt
        relatedEventIds
        relatedPersonIds
        relatedHorseIds
        sanctionedPersonId
        sanctionedHorseId
        allowedMutations
      }
    `
  },
  methods: {
    sanctionEdit(sanction) {
      this.$refs.sanctionDialog.setFormFields(sanction);
    },
    refetchQuery() {
      if (!this.sanctionedEntity) return undefined; // If prop has no value, skip. unneeded query thats not going to be used
      const id = this.personId || this.horseId;
      const query =
        this.sanctionedEntity == "Person"
          ? "person"
          : this.sanctionedEntity == "Horse"
            ? "horse"
            : "";
      return {
        query: gql`
            query ${query}($id: ID) {
              ${query}(id: $id) {
                id
                suspended
                sanctions {
                  ${this.$options.fragments.sanctionFields}
                }
              }
            }
          `,
        variables: { id }
      };
    }
  },
  apollo: {
    sanctionReasons: {
      query: gql`
        query sanctionReasons {
          sanctionReasons {
            id
            name
          }
        }
      `,
      fetchPolicy: "cache-first"
    }
  }
};
</script>
